/* ============================================================
   SHE GLEAM — theme.js v3.0
   Minimal, performance-first vanilla JavaScript
   Zero dependencies. All interactions native.
   ============================================================ */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     0. DYNAMIC TOPBAR HEIGHT
     Measures the .site-top element's real rendered
     height (announcement + marquee) and writes it
     into --topbar-height and --full-header-offset.
     This handles text wrapping on mobile, custom
     announcement length, font size differences, etc.
     Runs on load AND on every resize (debounced).
  ───────────────────────────────────────────── */
  function applyTopbarHeight() {
    const siteTop = document.querySelector('.site-top');
    const hdr     = document.getElementById('site-header');
    if (!siteTop) return;

    const topH   = siteTop.getBoundingClientRect().height;
    const hdrH   = hdr ? hdr.getBoundingClientRect().height : 68;
    const fullH  = topH + hdrH;

    const root = document.documentElement;
    root.style.setProperty('--topbar-height',      topH  + 'px');
    root.style.setProperty('--header-height',      hdrH  + 'px');
    root.style.setProperty('--full-header-offset', fullH + 'px');

    /* NOTE: header top is now handled purely by CSS var(--topbar-height)
       so it stays in sync with the announcement bar automatically */
  }

  // Run immediately (synchronous layout read after CSS paint)
  applyTopbarHeight();

  // Debounced resize handler
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyTopbarHeight, 120);
  }, { passive: true });

  // Re-measure after web fonts load (prevents gap caused by font metrics changing)
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(applyTopbarHeight);
  }
  setTimeout(applyTopbarHeight, 500);

  /* ─────────────────────────────────────────────
     1. HEADER: scroll shadow + hide-on-scroll-down
  ───────────────────────────────────────────── */
  const header = document.getElementById('site-header');
  if (header) {
    let lastScroll = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('scrolled', y > 20);
      // Hide header on scroll down, reveal on scroll up (desktop only)
      if (window.innerWidth > 768 && y > 120) {
        header.classList.toggle('hidden', y > lastScroll);
      } else {
        header.classList.remove('hidden');
      }
      lastScroll = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────────────────────────────────────────
     2. INTERSECTION OBSERVER: reveal on scroll
  ───────────────────────────────────────────── */
  const revealEls = document.querySelectorAll('.reveal');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            revealObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    revealEls.forEach((el) => revealObs.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('visible'));
  }

  /* ─────────────────────────────────────────────
     3. CART DRAWER
  ───────────────────────────────────────────── */
  const cartDrawer  = document.getElementById('cart-drawer');
  const cartOverlay = document.getElementById('cart-overlay');
  const cartClose   = document.getElementById('cart-drawer-close');

  function openCart() {
    cartDrawer  && cartDrawer.classList.add('open');
    cartOverlay && cartOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeCart() {
    cartDrawer  && cartDrawer.classList.remove('open');
    cartOverlay && cartOverlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-cart-open]').forEach((b) => b.addEventListener('click', openCart));
  cartClose   && cartClose.addEventListener('click', closeCart);
  cartOverlay && cartOverlay.addEventListener('click', closeCart);

  /* Cart qty +/− and Remove (AJAX) — optimistic updates, fast feel */
  async function refreshCartTotals() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCount(cart.item_count);
      updateShippingBar(cart.total_price);
      const subtotalEl  = document.querySelector('.cart-subtotal-price');
      const checkoutBtn = document.getElementById('cart-checkout');
      const footer      = document.querySelector('.cart-drawer-footer');
      if (subtotalEl)  subtotalEl.textContent = formatMoney(cart.total_price);
      if (checkoutBtn) checkoutBtn.textContent = 'Checkout — ' + formatMoney(cart.total_price);
      if (cart.item_count === 0) {
        await renderCart(); /* full render only when emptied */
      } else if (footer) {
        footer.style.display = '';
      }
    } catch {}
  }

  document.addEventListener('click', async (e) => {
    const minus  = e.target.closest('.cart-qty-minus');
    const plus   = e.target.closest('.cart-qty-plus');
    const remove = e.target.closest('.cart-item-remove');
    const btn    = minus || plus || remove;
    if (!btn) return;

    const key  = btn.dataset.key;
    if (!key) return;

    const item = btn.closest('.cart-item');

    if (remove) {
      /* Optimistic remove: animate out immediately */
      if (item) {
        item.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
        item.style.opacity = '0';
        item.style.transform = 'translateX(20px)';
      }
      try {
        await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: key, quantity: 0 }),
        });
        if (item) setTimeout(function(){ item.remove(); }, 260);
        await refreshCartTotals();
      } catch(err) {
        if (item) { item.style.opacity = '1'; item.style.transform = ''; }
      }
      return;
    }

    /* Quantity +/- */
    const qtyEl = item && item.querySelector('.cart-qty span');
    const current = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
    const quantity = minus ? Math.max(1, current - 1) : current + 1;

    /* Optimistic: update number immediately */
    if (qtyEl) qtyEl.textContent = quantity;
    btn.disabled = true;

    try {
      await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: key, quantity }),
      });
      await refreshCartTotals();
    } catch(err) {
      /* Revert on failure */
      if (qtyEl) qtyEl.textContent = current;
    } finally {
      btn.disabled = false;
    }
  });

  /* ── Core: fetch cart JSON and fully re-render the drawer ── */
  async function renderCart() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();

      updateCartCount(cart.item_count);
      updateShippingBar(cart.total_price);

      const body     = document.getElementById('cart-drawer-body');
      const footer   = document.querySelector('.cart-drawer-footer');
      if (!body) return;

      if (cart.item_count === 0) {
        body.innerHTML = `
          <div class="cart-empty-msg">
            <h3>Your bag is empty</h3>
            <p>Add something beautiful to begin your glow journey.</p>
            <a href="/collections/all" class="btn btn-gold" id="cart-empty-cta">Shop the Glow</a>
          </div>`;
        if (footer) footer.style.display = 'none';
      } else {
        /* Build items HTML */
        let html = '';
        cart.items.forEach(item => {
          /* Shopify CDN image sizing: insert _150x150 before the extension */
          let imgSrc = '';
          if (item.image) {
            imgSrc = item.image.split('?')[0]; // strip query params
            const dotIdx = imgSrc.lastIndexOf('.');
            if (dotIdx > -1) {
              imgSrc = imgSrc.slice(0, dotIdx) + '_150x150' + imgSrc.slice(dotIdx);
            }
          }
          const variant = item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item-variant">${item.variant_title}</div>` : '';
          html += `
            <div class="cart-item" data-key="${item.key}">
              <div class="cart-item-img">
                ${imgSrc ? `<a href="${item.url}"><img src="${imgSrc}" alt="${item.title}" width="150" loading="lazy"></a>` : ''}
              </div>
              <div>
                <div class="cart-item-title"><a href="${item.url}" style="color: inherit; text-decoration: none;">${item.product_title}</a></div>
                ${variant}
                <div class="cart-item-price">${formatMoney(item.final_price)}</div>
                <div class="cart-qty">
                  <button class="cart-qty-minus" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                  <span>${item.quantity}</span>
                  <button class="cart-qty-plus" data-key="${item.key}" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <button class="cart-item-remove" data-key="${item.key}" aria-label="Remove ${item.title}">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M11 3L3 11M3 3l8 8"/>
                </svg>
              </button>
            </div>`;
        });
        html += `
          <div class="cart-upsell">
            <p class="cart-upsell-title">✦ Complete Your Glow</p>
            <p style="font-size:0.82rem;color:var(--text-light);">Pair with our best-selling accessories for the full look.</p>
          </div>`;
        body.innerHTML = html;

        /* Update footer prices */
        const subtotalEl  = document.querySelector('.cart-subtotal-price');
        const checkoutBtn = document.getElementById('cart-checkout');
        if (subtotalEl)  subtotalEl.textContent  = formatMoney(cart.total_price);
        if (checkoutBtn) checkoutBtn.textContent  = `Checkout — ${formatMoney(cart.total_price)}`;
        if (footer) footer.style.display = '';
      }
    } catch(err) { /* silent fail */ }
  }

  function updateCartCount(count) {
    document.querySelectorAll('.cart-count').forEach((b) => {
      b.textContent = count;
      b.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  async function updateCartCountFromAPI() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCartCount(cart.item_count);
      updateShippingBar(cart.total_price);
    } catch {}
  }
  updateCartCountFromAPI();

  /* Free Shipping Bar */
  function updateShippingBar(totalPrice) {
    const fill = document.querySelector('.cart-shipping-fill');
    const text = document.querySelector('.cart-shipping-text');
    if (!fill || !text) return;

    const threshold = parseInt(fill.dataset.threshold || '10000'); // in paise/cents
    const pct       = Math.min(100, Math.round((totalPrice / threshold) * 100));
    fill.style.width = pct + '%';

    if (pct >= 100) {
      text.innerHTML = 'You have unlocked <strong>Free Shipping</strong>! 🎉';
    } else {
      const away = ((threshold - totalPrice) / 100).toFixed(2);
      text.innerHTML = `£${away} away from <strong>Free Shipping</strong>`;
    }
  }

  /* ─────────────────────────────────────────────
     4. MOBILE NAV
  ───────────────────────────────────────────── */
  const mobileNav     = document.getElementById('mobile-nav');
  const mobileOpen    = document.getElementById('mobile-menu-btn');
  const mobileClose   = document.getElementById('mobile-nav-close');
  const mobileOverlay = document.getElementById('mobile-nav-overlay');

  function openMobileNav() {
    mobileNav     && mobileNav.classList.add('open');
    mobileOverlay && mobileOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    mobileOpen    && mobileOpen.setAttribute('aria-expanded', 'true');
  }
  function closeMobileNav() {
    mobileNav     && mobileNav.classList.remove('open');
    mobileOverlay && mobileOverlay.classList.remove('open');
    document.body.style.overflow = '';
    mobileOpen    && mobileOpen.setAttribute('aria-expanded', 'false');
  }
  mobileOpen    && mobileOpen.addEventListener('click', openMobileNav);
  mobileClose   && mobileClose.addEventListener('click', closeMobileNav);
  mobileOverlay && mobileOverlay.addEventListener('click', closeMobileNav);

  /* ─────────────────────────────────────────────
     5. SEARCH OVERLAY
  ───────────────────────────────────────────── */
  const searchOverlay = document.getElementById('search-overlay');
  const searchClose   = document.getElementById('search-close');
  const searchInput   = searchOverlay && searchOverlay.querySelector('input');

  document.querySelectorAll('[data-search-open]').forEach((b) => {
    b.addEventListener('click', () => {
      searchOverlay && searchOverlay.classList.add('open');
      setTimeout(() => searchInput && searchInput.focus(), 50);
    });
  });
  searchClose && searchClose.addEventListener('click', () => {
    searchOverlay && searchOverlay.classList.remove('open');
  });
  searchOverlay && searchOverlay.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') searchOverlay.classList.remove('open');
  });

  /* ─────────────────────────────────────────────
     6. PRODUCT GALLERY: vertical strip + arrows + lightbox
  ───────────────────────────────────────────── */
  var galleryMain  = document.getElementById('product-gallery-main');
  var mainImg      = document.getElementById('product-main-img');
  var thumbs       = document.querySelectorAll('.product-thumb');
  var galleryPrev  = document.getElementById('gallery-prev');
  var galleryNext  = document.getElementById('gallery-next');
  var lightbox     = document.getElementById('gallery-lightbox');
  var lightboxImg  = document.getElementById('lightbox-img');
  var lightboxClose= document.getElementById('lightbox-close');
  var lightboxDots = document.getElementById('lightbox-dots');

  var galleryImages = [];
  var currentImgIdx = 0;
  if (galleryMain) {
    try { 
      galleryImages = JSON.parse(galleryMain.dataset.images || '[]'); 
      // Preload images for instant switching
      galleryImages.forEach(function(src) {
        var img = new Image();
        img.src = src;
      });
    } catch (e) {}
  }

  function switchGalleryImage(idx, fromThumb) {
  if (!galleryImages.length || !mainImg) return;
  idx = ((idx % galleryImages.length) + galleryImages.length) % galleryImages.length;
  currentImgIdx = idx;

  mainImg.style.opacity = '0';
  mainImg.style.transform = 'scale(0.98)';
  setTimeout(function () {
    mainImg.src = galleryImages[idx];
    mainImg.style.opacity = '1';
    mainImg.style.transform = 'scale(1)';
  }, 40);

  /* Sync thumbs */
  thumbs.forEach(function (t) {
    t.classList.toggle('active', Number(t.dataset.index) === idx);
  });

  /* Sync lightbox dots */
  if (lightboxDots) {
    lightboxDots.querySelectorAll('span').forEach(function (d, i) {
      d.classList.toggle('active', i === idx);
    });
  }

  /* Desktop Grid Scroll Sync */
  if (window.matchMedia('(min-width: 1025px)').matches) {
    var desktopGridItem = document.querySelector('.gallery-grid-item[data-image-index="' + idx + '"]');
    if (desktopGridItem) {
      desktopGridItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

if (mainImg) {
  mainImg.style.transition = 'opacity 0.16s ease, transform 0.16s ease';
}

  /* Thumbnail clicks */
  thumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      switchGalleryImage(Number(thumb.dataset.index), true);
    });
  });

  /* Arrow buttons */
  galleryPrev && galleryPrev.addEventListener('click', function () {
    switchGalleryImage(currentImgIdx - 1);
  });
  galleryNext && galleryNext.addEventListener('click', function () {
    switchGalleryImage(currentImgIdx + 1);
  });

  /* Keyboard arrow navigation when focused on gallery */
  document.addEventListener('keydown', function (e) {
    if (!galleryMain) return;
    if (e.key === 'ArrowLeft')  switchGalleryImage(currentImgIdx - 1);
    if (e.key === 'ArrowRight') switchGalleryImage(currentImgIdx + 1);
  });

    /* Swipe on mobile main image */
  var touchStartX = null;
  var touchStartY = null;
  if (galleryMain) {
    galleryMain.addEventListener('touchstart', function (e) {
      if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }
    }, { passive: true });
    galleryMain.addEventListener('touchend', function (e) {
      if (touchStartX === null || touchStartY === null) return;
      var touchEndX = e.changedTouches[0].clientX;
      var touchEndY = e.changedTouches[0].clientY;
      var dx = touchEndX - touchStartX;
      var dy = touchEndY - touchStartY;
      
      // Reset values immediately to prevent stale coordinates
      touchStartX = null;
      touchStartY = null;

      // Only trigger if horizontal swipe is larger than vertical movement, and swipe is > 50px
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        dx > 0 ? switchGalleryImage(currentImgIdx - 1) : switchGalleryImage(currentImgIdx + 1);
      }
    }, { passive: true });
  }

  /* Desktop zoom on hover */
  if (galleryMain && window.matchMedia('(min-width: 768px)').matches) {
    galleryMain.addEventListener('mousemove', function (e) {
      var img = galleryMain.querySelector('img');
      if (!img) return;
      var r = galleryMain.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
    });
    galleryMain.addEventListener('mouseenter', function () {
      var img = galleryMain.querySelector('img');
      if (img) img.style.transform = 'scale(1.55)';
    });
    galleryMain.addEventListener('mouseleave', function () {
      var img = galleryMain.querySelector('img');
      if (img) { img.style.transform = 'scale(1)'; img.style.transformOrigin = 'center'; }
    });
  }

  /* Mobile tap-to-lightbox */
  if (lightbox && lightboxImg && galleryMain) {
    /* Build dots */
    if (lightboxDots && galleryImages.length > 1) {
      galleryImages.forEach(function (_, i) {
        var dot = document.createElement('span');
        dot.addEventListener('click', function () { switchGalleryImage(i); lightboxImg.src = galleryImages[i]; });
        lightboxDots.appendChild(dot);
      });
    }

    galleryMain.addEventListener('click', function () {
      if (window.matchMedia('(max-width: 768px)').matches) {
        lightboxImg.src = galleryImages[currentImgIdx] || mainImg.src;
        lightbox.classList.add('open');
        document.body.style.overflow = 'hidden';
        /* Sync dots */
        if (lightboxDots) {
          lightboxDots.querySelectorAll('span').forEach(function (d, i) {
            d.classList.toggle('active', i === currentImgIdx);
          });
        }
      }
    });
        /* Desktop Grid click-to-zoom */
    document.querySelectorAll('.product-gallery-desktop .gallery-grid-item').forEach(function (item) {
      item.addEventListener('click', function () {
        var idx = Number(item.dataset.imageIndex);
        currentImgIdx = idx;
        lightboxImg.src = galleryImages[idx] || item.querySelector('img').src;
        lightbox.classList.add('open');
        document.body.style.overflow = 'hidden';
        /* Sync dots */
        if (lightboxDots) {
          lightboxDots.querySelectorAll('span').forEach(function (d, i) {
            d.classList.toggle('active', i === idx);
          });
        }
      });
    });

    /* Lightbox Swipe */
    var lbTouchStartX = 0;
    lightbox.addEventListener('touchstart', function (e) {
      lbTouchStartX = e.changedTouches[0].clientX;
    }, { passive: true });
    lightbox.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - lbTouchStartX;
      if (Math.abs(dx) > 40) {
        dx > 0 ? switchGalleryImage(currentImgIdx - 1) : switchGalleryImage(currentImgIdx + 1);
        lightboxImg.src = galleryImages[currentImgIdx];
      }
    }, { passive: true });

    /* Lightbox Arrows */
    var lbPrev = document.getElementById('lightbox-prev');
    var lbNext = document.getElementById('lightbox-next');
    if (lbPrev) {
      lbPrev.addEventListener('click', function(e) {
        e.stopPropagation();
        switchGalleryImage(currentImgIdx - 1);
        lightboxImg.src = galleryImages[currentImgIdx];
      });
    }
    if (lbNext) {
      lbNext.addEventListener('click', function(e) {
        e.stopPropagation();
        switchGalleryImage(currentImgIdx + 1);
        lightboxImg.src = galleryImages[currentImgIdx];
      });
    }

    function closeLightbox() {
      lightbox.classList.remove('open');
      document.body.style.overflow = '';
    }
    lightboxClose && lightboxClose.addEventListener('click', closeLightbox);
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
  }

  /* ─────────────────────────────────────────────
     7. VARIANT SELECTOR
  ───────────────────────────────────────────── */
  const variantData = document.getElementById('product-variants-json');
  const variants    = variantData ? JSON.parse(variantData.textContent) : [];
  const atcBtn      = document.getElementById('add-to-cart-btn');

  document.querySelectorAll('.variant-btn, .color-swatch').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const group = btn.closest('.variant-group');
      if (group) group.querySelectorAll('.variant-btn, .color-swatch').forEach((b) => {
        b.classList.remove('selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('selected');
      btn.setAttribute('aria-pressed', 'true');
      updateVariantUI();
    });
  });

  /* Initialise availability state on page load */
  updateVariantUI();

  function getSelectedOptions() {
    const opts = [];
    document.querySelectorAll('.variant-group').forEach((group) => {
      const idx = parseInt(group.dataset.optionIndex, 10);
      const sel = group.querySelector('.variant-btn.selected, .color-swatch.selected');
      if (!isNaN(idx) && sel) opts[idx] = sel.dataset.value;
    });
    return opts;
  }

  function findVariant(opts) {
    return variants.find((v) => {
      return v.options.every((o, i) => opts[i] === o);
    });
  }

    function updateVariantUI() {
    const opts    = getSelectedOptions();
    const variant = variants.length ? findVariant(opts) : null;

    // Update availability of all option buttons/swatches
    document.querySelectorAll('.variant-group').forEach((group) => {
      const idx = parseInt(group.dataset.optionIndex, 10);
      const buttons = group.querySelectorAll('.variant-btn, .color-swatch');
      buttons.forEach((btn) => {
        const testOpts = opts.slice();
        testOpts[idx] = btn.dataset.value;
        const testVariant = variants.length ? findVariant(testOpts) : null;
        const unavailable = !testVariant || !testVariant.available;
        btn.disabled = unavailable;
        btn.classList.toggle('unavailable', unavailable);
        if (unavailable) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    });

    if (!variant) return;

    // Update variant image safely
    try {
      if (variant.featured_image) {
        let idx = -1;
        if (variant.featured_image.position != null) {
          idx = variant.featured_image.position - 1;
        }
        
        if (typeof switchGalleryImage === 'function' && typeof galleryImages !== 'undefined' && idx >= 0 && idx < galleryImages.length) {
          switchGalleryImage(idx);
        } else {
          const mImg = document.getElementById('product-main-img');
          if (mImg && variant.featured_image.src) mImg.src = variant.featured_image.src;
        }
      }
    } catch (e) {
      console.warn("Variant image switch error:", e);
    }

    // Update price
    const priceEl   = document.getElementById('product-price');
    const compareEl = document.querySelector('.product-price-compare');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }
    if (compareEl && variant.compare_at_price > variant.price) {
      compareEl.textContent = formatMoney(variant.compare_at_price);
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.style.display = 'none';
    }

    const atcBtn = document.getElementById('add-to-cart-btn');
if (atcBtn) {
  atcBtn.dataset.variantId = variant.id;
  atcBtn.dataset.inventory = variant.inventory_quantity != null ? variant.inventory_quantity : 9999;
  atcBtn.disabled          = !variant.available;
  atcBtn.textContent       = variant.available ? 'Add to Bag' : 'Sold Out';
}

const buyNowBtn = document.getElementById('buy-now-btn');
if (buyNowBtn) {
  buyNowBtn.dataset.variantId = variant.id;
  buyNowBtn.disabled          = !variant.available;
}

    // Update sticky ATC price
    const stickyPrice = document.querySelector('.sticky-atc-price');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
  }


  function formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ─────────────────────────────────────────────
     8. ADD TO CART (AJAX)
  ───────────────────────────────────────────── */
  if (atcBtn) {
    atcBtn.addEventListener('click', async () => {
      const variantId = atcBtn.dataset.variantId;
      if (!variantId) return;

      const qtyInput  = document.getElementById('qty-input');
      const reqQty    = qtyInput ? (parseInt(qtyInput.value) || 1) : 1;
      const maxQty    = parseInt(atcBtn.getAttribute('data-inventory') || '9999');
      const availMsg  = document.getElementById('atc-avail-msg');

      if (availMsg) {
        availMsg.classList.remove('show');
        availMsg.textContent = '';
      }
      if (maxQty !== 9999 && reqQty > maxQty) {
        if (availMsg) {
          availMsg.textContent = maxQty === 0
            ? 'This piece is currently sold out.'
            : 'Only ' + maxQty + ' available — please adjust your quantity.';
          availMsg.classList.add('show');
        }
        return; /* block the add */
      }

      const orig = atcBtn.textContent;
      atcBtn.textContent = 'Adding...';
      atcBtn.disabled    = true;

      /* Collect gift note if filled */
      const giftCheck = document.getElementById('gift-checkbox');
      const giftNote  = document.getElementById('gift-note-text');
      const isGift    = giftCheck && giftCheck.checked;
      const noteVal   = giftNote && giftNote.value.trim() ? giftNote.value.trim() : null;

      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: [{ id: Number(variantId), quantity: reqQty }] }),
        });

        /* If gift: save note + "Gift Wrap" attribute so merchant sees it in Admin */
        if (isGift) {
          const updates = { attributes: { 'Gift Wrap': 'Yes' } };
          if (noteVal) updates.note = noteVal;
          await fetch('/cart/update.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(updates),
          });
        }

        if (res.ok) {
          atcBtn.textContent = '✓ Added!';
          await renderCart();
          setTimeout(openCart, 350);
        } else {
          atcBtn.textContent = 'Try Again';
        }
      } catch {
        atcBtn.textContent = 'Error';
      }

      setTimeout(() => {
        atcBtn.textContent = orig;
        atcBtn.disabled    = false;
      }, 2200);
    });
  }

  /* Sticky ATC button */
  const stickyAtcBtn     = document.querySelector('.sticky-atc-btn');
  const stickyAtc        = document.getElementById('sticky-atc');
  const productAtcBlock  = document.querySelector('.product-atc-block');

  if (stickyAtcBtn) {
    stickyAtcBtn.addEventListener('click', () => atcBtn && atcBtn.click());
  }
  if (stickyAtc && productAtcBlock && 'IntersectionObserver' in window) {
    const stickyObs = new IntersectionObserver(
      ([e]) => { stickyAtc.style.display = e.isIntersecting ? 'none' : 'flex'; },
      { threshold: 0 }
    );
    stickyObs.observe(productAtcBlock);
  }

  /* ─────────────────────────────────────────────
     8b. WISHLIST (localStorage)
  ───────────────────────────────────────────── */
  var WL_KEY     = 'she_gleam_wishlist';
  var wlBtn      = document.getElementById('wishlist-btn');
  var wlIcon     = document.getElementById('wishlist-icon');
  var wlLabel    = document.getElementById('wishlist-label');

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch(e) { return []; }
  }
  function saveWishlist(list) { localStorage.setItem(WL_KEY, JSON.stringify(list)); }
  function isWishlisted(id) { return getWishlist().some(function(p){ return p.id === id; }); }

  function syncWishlistUI(id) {
    var wishlisted = isWishlisted(id);
    if (wlIcon) wlIcon.setAttribute('fill', wishlisted ? 'var(--gold)' : 'none');
    if (wlLabel) wlLabel.textContent = wishlisted ? 'Saved to Wishlist ♥' : 'Save to Wishlist';
    if (wlBtn) wlBtn.style.borderColor = wishlisted ? 'var(--gold)' : '';
    if (wlBtn) wlBtn.style.color = wishlisted ? 'var(--gold)' : '';
  }

  if (wlBtn) {
    var pid = Number(wlBtn.dataset.productId);
    syncWishlistUI(pid);

    wlBtn.addEventListener('click', function () {
      var list = getWishlist();
      if (isWishlisted(pid)) {
        list = list.filter(function(p){ return p.id !== pid; });
        saveWishlist(list);
      } else {
        list.push({
          id:    pid,
          title: wlBtn.dataset.productTitle,
          url:   wlBtn.dataset.productUrl,
          price: wlBtn.dataset.productPrice,
          image: wlBtn.dataset.productImage
        });
        saveWishlist(list);
        /* quick heart pulse animation */
        wlBtn.style.transform = 'scale(1.06)';
        setTimeout(function(){ wlBtn.style.transform = ''; }, 200);
      }
      syncWishlistUI(pid);
    });
  }

  /* ─────────────────────────────────────────────
     8c. GIFT TOGGLE
  ───────────────────────────────────────────── */
  var giftCheckbox = document.getElementById('gift-checkbox');
  var giftNoteWrap = document.getElementById('gift-note-wrap');
  if (giftCheckbox && giftNoteWrap) {
    giftCheckbox.addEventListener('change', function () {
      giftNoteWrap.classList.toggle('visible', giftCheckbox.checked);
    });
  }

  /* ─────────────────────────────────────────────
     9. PRODUCT ACCORDIONS
  ───────────────────────────────────────────── */
  document.querySelectorAll('.accordion-header').forEach((header) => {
    header.addEventListener('click', () => {
      const acc = header.closest('.accordion');
      if (!acc) return;
      const isOpen = acc.classList.contains('open');
      // Close all first
      document.querySelectorAll('.accordion').forEach((a) => a.classList.remove('open'));
      if (!isOpen) acc.classList.add('open');
    });
  });

  /* ─────────────────────────────────────────────
     10. NEWSLETTER FORM (AJAX)
  ───────────────────────────────────────────── */
  const newsletterForm = document.getElementById('newsletter-form');
  const newsletterMsg  = document.getElementById('newsletter-msg');

  if (newsletterForm) {
    newsletterForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const emailInput = newsletterForm.querySelector('input[type="email"]');
      const submitBtn  = newsletterForm.querySelector('button[type="submit"]');
      if (!emailInput || !emailInput.value) return;

      const origText = submitBtn.textContent;
      submitBtn.textContent = 'Joining...';
      submitBtn.disabled    = true;

      const body = new FormData();
      body.append('contact[email]', emailInput.value);
      body.append('form_type', 'customer');

      try {
        await fetch('/contact', { method: 'POST', body });
        if (newsletterMsg) {
          newsletterMsg.textContent = '✦ You are now part of the She Gleam Circle.';
          newsletterMsg.classList.add('show');
        }
        emailInput.value = '';
      } catch {
        if (newsletterMsg) {
          newsletterMsg.textContent = 'Something went wrong. Please try again.';
          newsletterMsg.classList.add('show');
        }
      }

      setTimeout(() => {
        submitBtn.textContent = origText;
        submitBtn.disabled    = false;
        newsletterMsg && newsletterMsg.classList.remove('show');
      }, 4000);
    });
  }

  /* 11. Collection filter buttons — handled by inline script in featured-collection.liquid */

  /* ─────────────────────────────────────────────
     12. SCARCITY BAR FILL (IntersectionObserver)
  ───────────────────────────────────────────── */
  const fills = document.querySelectorAll('.scarcity-fill');
  if (fills.length && 'IntersectionObserver' in window) {
    const fillObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.width = e.target.dataset.fill || '78%';
            fillObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    fills.forEach((f) => fillObs.observe(f));
  }

  /* ─────────────────────────────────────────────
     13. KEYBOARD ACCESSIBILITY: ESC key
  ───────────────────────────────────────────── */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      closeMobileNav();
      searchOverlay && searchOverlay.classList.remove('open');
    }
  });

  /* ─────────────────────────────────────────────
     14. FOCUS TRAP FOR CART DRAWER
  ───────────────────────────────────────────── */
  if (cartDrawer) {
    cartDrawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = cartDrawer.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  window.renderCart = renderCart;

})();

/* ============================================================
   ROUND 2+3 FIXES
   ============================================================ */
(function () {
  'use strict';

  /* ── Qty selector +/- ── */
  var qtyMinus = document.querySelector('.qty-minus');
  var qtyPlus  = document.querySelector('.qty-plus');
  var qtyInput = document.getElementById('qty-input');
  if (qtyMinus && qtyPlus && qtyInput) {
    qtyMinus.addEventListener('click', function () {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value) || 1) - 1);
    });
    qtyPlus.addEventListener('click', function () {
      qtyInput.value = Math.min(99, (parseInt(qtyInput.value) || 1) + 1);
    });
    qtyInput.addEventListener('change', function () {
      var v = parseInt(this.value);
      if (isNaN(v) || v < 1) this.value = 1;
      if (v > 99) this.value = 99;
    });
  }

  /* ── ATC: availability check + cart open on mobile ── */
  var openCart    = window.__sheGleamOpenCart || function () {
    var cartDrawer = document.getElementById('cart-drawer');
    var cartOverlay= document.getElementById('cart-overlay');
    if (cartDrawer) { cartDrawer.classList.add('open'); cartDrawer.removeAttribute('hidden'); }
    if (cartOverlay) cartOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  /* ── Card Quick Add to Cart ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.card-quick-add');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    btn.classList.add('adding');
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    })
    .then(function () {
      btn.classList.remove('adding');
      btn.classList.add('added');
      /* Refresh cart drawer */
      if (typeof window.renderCart === 'function') {
        window.renderCart().then(function () { openCart(); });
      } else {
        setTimeout(openCart, 200);
      }
      setTimeout(function () {
        btn.classList.remove('added');
        btn.disabled = false;
      }, 2200);
    })
    .catch(function () {
  btn.classList.remove('adding');
  btn.classList.remove('added');
  btn.disabled = false;
  btn.setAttribute('aria-label', 'Could not add item. Please try again.');
});
  });

  /* ── Wishlist card buttons ── */
  var WL_KEY = 'she_gleam_wishlist';
  function getWL()     { try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch(e){ return []; } }
  function saveWL(l)   { localStorage.setItem(WL_KEY, JSON.stringify(l)); }
  function isInWL(id)  { return getWL().some(function (p) { return String(p.id) === String(id); }); }

   /* Scan cards and mark ones already in the wishlist */
  function markWishlisted(root) {
    (root || document).querySelectorAll('.card-wishlist').forEach(function (btn) {
      var card = btn.closest('.product-card');
      if (!card) return;
      var pid = card.getAttribute('data-product-id');
      if (pid && isInWL(pid)) btn.classList.add('wishlisted');
    });
  }
  markWishlisted(); /* initial pass */

  /* Event-delegated wishlist click (works on AJAX-loaded cards) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.card-wishlist');
    if (!btn) return;
    e.preventDefault(); e.stopPropagation();
    var card   = btn.closest('.product-card');
    if (!card) return;
    var pid    = card.getAttribute('data-product-id');
    var ptitle = card.getAttribute('data-product-title');
    var purl   = card.getAttribute('data-product-url');
    var pprice = card.getAttribute('data-product-price');
    var pimg   = card.getAttribute('data-product-image');
    if (!pid) return;
    var list = getWL();
    if (isInWL(pid)) {
      list = list.filter(function (p) { return String(p.id) !== String(pid); });
      btn.classList.remove('wishlisted');
    } else {
      list.push({ id: pid, title: ptitle, url: purl, price: pprice, image: pimg });
      btn.classList.add('wishlisted');
      btn.style.transform = 'scale(1.2)';
      setTimeout(function () { btn.style.transform = ''; }, 180);
    }
    saveWL(list);
  });

  /* Expose for AJAX-loaded cards to mark initial wishlist state */
  window.bindWishlist = function (root) { markWishlisted(root); };

  /* ── Mobile: site-top scrolls naturally, header fixed at top:0 via CSS ── */
  var siteTop    = document.querySelector('.site-top');
  var siteHeader = document.getElementById('site-header');

  if (siteTop && siteHeader) {
    window.addEventListener('scroll', function () {
      if (window.innerWidth > 767) {
        siteTop.classList.remove('scrolled-down');
        siteHeader.style.top = '';
      }
    }, { passive: true });
  }

  /* ── Rotating announcement bar ── */
  var announcements = document.querySelectorAll('.announce-msg');
  if (announcements.length > 1) {
    var annIdx = 0;
    setInterval(function () {
      announcements[annIdx].classList.remove('active');
      announcements[annIdx].setAttribute('aria-hidden', 'true');
      annIdx = (annIdx + 1) % announcements.length;
      announcements[annIdx].classList.add('active');
      announcements[annIdx].setAttribute('aria-hidden', 'false');
    }, 4000);
  }

})();

(function() {
  var buyNowBtn = document.getElementById('buy-now-btn');
  if (!buyNowBtn) return;

  buyNowBtn.addEventListener('click', function() {
    var variantId = buyNowBtn.getAttribute('data-variant-id');
    if (!variantId) return;

    buyNowBtn.disabled = true;
    buyNowBtn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
    .then(function(response) {
      if (!response.ok) throw new Error('add failed');
      window.location.href = '/checkout';
    })
    .catch(function() {
      buyNowBtn.disabled = false;
      buyNowBtn.textContent = 'Try Again';
    });
  });
})();
/* ── Live viewer count ─────────────────────────── */
(function () {
  var el = document.getElementById('viewer-count');
  if (!el) return;
  var count = Math.floor(Math.random() * 15) + 5;
  el.textContent = count;
  function drift() {
    var change = Math.floor(Math.random() * 3) + 1;
    count += Math.random() > 0.5 ? change : -change;
    if (count < 3) count = 3 + Math.floor(Math.random() * 3);
    if (count > 25) count = 25 - Math.floor(Math.random() * 3);
    el.textContent = count;
    var next = (Math.random() * 20000) + 20000;
    setTimeout(drift, next);
  }
  setTimeout(drift, (Math.random() * 20000) + 15000);
})();
/* ── She Gleam mobile drill-down navigation ───────────────── */
(function () {
  function initialiseMobileDrilldown() {
    var drawer = document.querySelector('[data-mobile-drilldown]');

    if (!drawer || drawer.dataset.drilldownReady === 'true') {
      return;
    }

    var panels = Array.prototype.slice.call(
      drawer.querySelectorAll('[data-mobile-panel]')
    );

    var triggers = Array.prototype.slice.call(
      drawer.querySelectorAll('[data-mobile-target]')
    );

    var backButton = drawer.querySelector('[data-mobile-back]');
    var title = drawer.querySelector('[data-mobile-title]');
    var closeButton = document.getElementById('mobile-nav-close');
    var openButton = document.getElementById('mobile-menu-btn');
    var overlay = document.getElementById('mobile-nav-overlay');
    var stack = ['root'];

    if (!panels.length || !backButton || !title) {
      return;
    }

    drawer.dataset.drilldownReady = 'true';

    function findPanel(panelId) {
      return drawer.querySelector(
        '[data-mobile-panel="' + panelId + '"]'
      );
    }

    function updatePanels() {
      var activeId = stack[stack.length - 1];
      var activePanel = findPanel(activeId);

      panels.forEach(function (panel) {
        var panelId = panel.getAttribute('data-mobile-panel');
        var panelIndex = stack.indexOf(panelId);
        var isActive = panelId === activeId;
        var isParent = panelIndex > -1 && !isActive;

        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('is-parent', isParent);
        panel.setAttribute(
          'aria-hidden',
          isActive ? 'false' : 'true'
        );
      });

      var inSubmenu = activeId !== 'root';

      drawer.classList.toggle(
        'is-submenu-open',
        inSubmenu
      );

      backButton.hidden = !inSubmenu;

      title.textContent = activePanel
        ? activePanel.getAttribute('data-mobile-panel-title') || ''
        : '';

      if (activePanel) {
        var panelScroll = activePanel.querySelector(
          '.sg-mobile-nav__panel-scroll'
        );

        if (panelScroll) {
          panelScroll.scrollTop = 0;
        }
      }
    }

    function openPanel(panelId) {
      if (!findPanel(panelId)) {
        return;
      }

      if (stack[stack.length - 1] !== panelId) {
        stack.push(panelId);
      }

      updatePanels();
    }

    function goBack() {
      if (stack.length > 1) {
        stack.pop();
      }

      updatePanels();
    }

    function resetPanels() {
      stack = ['root'];
      updatePanels();
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        openPanel(
          trigger.getAttribute('data-mobile-target')
        );
      });
    });

    backButton.addEventListener('click', goBack);

    if (openButton) {
      openButton.addEventListener(
        'click',
        resetPanels
      );
    }

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        window.setTimeout(
          resetPanels,
          320
        );
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function () {
        window.setTimeout(
          resetPanels,
          320
        );
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') {
        return;
      }

      if (stack.length > 1) {
        event.preventDefault();
        goBack();
      }
    });

    updatePanels();
  }

  initialiseMobileDrilldown();

  document.addEventListener(
    'shopify:section:load',
    function () {
      initialiseMobileDrilldown();
    }
  );
})();